import type { PlaidLinkError } from 'react-plaid-link';
import { PortfolioLinkingSDKError } from '../../error/PortfolioLinkingSDKError';
import { sentryCaptureMessage } from '@/components/Sentry';

/**
 * this is a mapping of Plaid error codes to user friendly error messages that we can display to the user.
 * these error codes were selected based on the most common errors that we think applicable to our users, by referring https://plaid.com/docs/errors/.
 * if the error code is not in this mapping, we will log it to sentry. so we can add it to the mapping later.
 */
const ErrorCodeMap: Record<
  PlaidLinkError['error_code'],
  {
    message: string;
  }
> = {
  ACCESS_NOT_GRANTED: {
    message:
      'Insufficient Sharing Permissions: There was an error connecting to your account. Try linking your account again by selecting the required information to share with this application.',
  },
  INSUFFICIENT_CREDENTIALS: {
    message: 'Access Denied: The authorization flow did not complete',
  },
  INVALID_CREDENTIALS: {
    message:
      'The credentials you provided were incorrect: Check that your credentials are the same that you use for this institution',
  },
  INVALID_MFA: {
    message:
      'The provided MFA responses were not correct.For security reasons, your account may be locked after several unsuccessful attempts',
  },
  INVALID_SEND_METHOD: {
    message: 'The provided MFA send method was invalid',
  },
  INVALID_PHONE_NUMBER: {
    message: `Invalid Phone Number: We couldn't verify the number you provided is a valid number. Please re-enter your phone number to try again.`,
  },
  INVALID_OTP: {
    message:
      'Security code incorrect: Check that the code you entered is the same code that was sent to you',
  },
  ITEM_LOCKED: {
    message: `Too many attempts: Your account is locked for security reasons. Please visit the institution's site and unlock your account`,
  },
  ITEM_LOGIN_REQUIRED: {
    message: `Username or password incorrect: If you've recently updated your account with this institution, be sure you're entering your updated credentials`,
  },
  ITEM_NOT_SUPPORTED: {
    message:
      'Your account is not currently supported. Please log in using a different account',
  },
  MFA_NOT_SUPPORTED: {
    message: `Your account could not be connected because the multi-factor authentication method it uses is not currently supported. Please try a different account.`,
  },
  NO_ACCOUNTS: {
    message:
      'Your credentials are correct, but we couldn’t find any accounts with this institution that are compatible with this application',
  },
  USER_SETUP_REQUIRED: {
    message:
      'Action required with your account: The account has not been fully set up. Please visit the institution’s website to complete the setup. Then restart the linking process again',
  },
  USER_INPUT_TIMEOUT: {
    message:
      'Session expired: Please re-enter your information to link your portfolio',
  },
  INSTITUTION_DOWN: {
    message:
      'The institution you selected is not currently responding. please try again soon',
  },
  INSTITUTION_NOT_RESPONDING: {
    message:
      'The institution you selected is not currently responding. please try again soon',
  },
  INSTITUTION_NOT_AVAILABLE: {
    message:
      'The institution you selected is not currently responding. please try again soon',
  },
  INSTITUTION_NO_LONGER_SUPPORTED: {
    message: 'The institution is no longer supported',
  },
  INSTITUTION_REGISTRATION_REQUIRED: {
    message: 'We are sorry, this institution is not supported',
  },
  RECAPTCHA_BAD: {
    message:
      'The provided recaptcha challenge response was denied. Please try again',
  },
  RATE_LIMIT: {
    message: 'Too many attempts. Please try again after few minutes',
  },
  INTERNAL_SERVER_ERROR: {
    message: 'Something went wrong! Please try again later.',
  },
  INVALID_FIELD: {
    message: 'Something went wrong! Please try again later.',
  },
  INVALID_LINK_TOKEN: {
    message: 'Something went wrong! Please try again later.',
  },
  OAUTH_STATE_ID_ALREADY_PROCESSED: {
    message: 'Something went wrong! Please try again later.',
  },
};

export const createPortfolioLinkingSDKError = (
  error: PlaidLinkError
): PortfolioLinkingSDKError => {
  const isMappedError = ErrorCodeMap.hasOwnProperty(error.error_code);
  if (!isMappedError) {
    sentryCaptureMessage({
      team: 'wombats',
      message: 'Unmapped Portfolio Linking SDK Error',
      extra: {
        provider: 'plaid',
        error,
      },
    });
  }

  return new PortfolioLinkingSDKError(
    'plaid',
    isMappedError
      ? ErrorCodeMap[error.error_code].message
      : 'Something went wrong! Please try again later.',
    error,
    error.error_code
  );
};
